import {Injectable} from '@angular/core';
import {APIService} from "./api.service";
import {Industry} from "../interfaces/industry";
import {Domain} from "../interfaces/domain";
import {Seniority} from "../interfaces/seniority";
import {Language} from "../interfaces/language";
import {DirectoryDescription} from "../interfaces/directoryDescription";
import {Directory, DirectoryStatus} from "../interfaces/directory";
import {Paged} from "../interfaces/paged";
import {ProjectRole} from "../interfaces/projectRole";
import {Market} from "../interfaces/market";
import {Question} from "../interfaces/question";
import {Department} from "../interfaces/department";
import {Skill} from "../interfaces/skill";
import {Certification} from "../interfaces/certification";
import {Development} from "../interfaces/development";


@Injectable({
  providedIn: 'root'
})
export class DirectoryService extends APIService{

  public async describeDirectory(dir: string){
    return await this.get<DirectoryDescription>('/' + dir + '/describe', 'Could not describe ' + dir);
  }

  public async find<T extends Directory>(dir: string, page: number = 1, filters = null, sorts:{col: string, dir: number}[] = []): Promise<Paged<T>>{
    return await this.post<any, Paged<T>>('/'+dir+'/find', {
      page,
      sorts,
      filters
    });
  }

  public async getDirectory<T extends Directory>(dir: string, page: number = 1, status: DirectoryStatus = DirectoryStatus.ACTIVE): Promise<Paged<T>>{
    return await this.get<Paged<T>>('/' + dir, 'Could not get ' + dir, {
      params: { page, status }
    });
  }

  public async getQuestions(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<Question[]>('/questions', 'Could not get Questions', {
      params: { status }
    });
  }

  public async getMarkets(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<Market[]>('/markets', 'Could not get Markets', {
      params: { status }
    });
  }

  public async getIndustries(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<Industry[]>('/industries', 'Could not get Industries', {
      params: { status }
    });
  }

  public async getDomains(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<Domain[]>('/domains', 'Could not get Domains', {
      params: { status }
    });
  }

  public async getDegrees(status: DirectoryStatus = DirectoryStatus.ACTIVE) {
    return await this.get<Industry[]>('/degrees', 'Could not get Degrees', {
      params: { status }
    });
  }

  public async getDepartments(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<Department[]>('/departments', 'Could not get Departments', {
      params: { status }
    });
  }

  public async getSeniority(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<Seniority[]>('/seniorities', 'Could not get Seniority', {
      params: { status }
    });
  }

  public async getLanguages(status: DirectoryStatus = DirectoryStatus.ACTIVE) {
    return await this.get<Language[]>('/languages', 'Could not get Languages', {
      params: { status }
    });
  }

  public async getProjectRoles(status: DirectoryStatus = DirectoryStatus.ACTIVE){
    return await this.get<ProjectRole[]>('/projectroles', 'Could not get Project Roles', {
      params: { status }
    });
  }

  public async changeDirectoryStatus(dir: string, id: number, status: DirectoryStatus = DirectoryStatus.ACTIVE) {
    const updates: Partial<Directory> = { status };
    return await this.saveDirectory(dir, id, updates, 'Item now ' + DirectoryStatus[status] + '!');
  }

  public async addDirectory(dir: string, updates:Partial<Directory>, savemessage = null){
    return await this.post<Partial<Directory>, Directory>('/'+dir, updates, savemessage);
  }

  public async saveDirectory(dir: string, id: number, updates:Partial<Directory>, savemessage = null) {
    return await this.put<Partial<Directory>, Directory>('/'+dir+'/' + id, updates, savemessage);
  }

  public async deleteDirectory(dir: string, id: number) {
    return await this.delete<Directory>('/'+dir+'/'+id, 'Item deleted!');
  }

  public async changeDirectorySort(dir: string, from: number, to: number, savemessage = null){
    return await this.put<any, Directory[]>('/'+dir+'/resort', {
      from, to
    }, savemessage);
  }

  /**
   * Get skills by an array of IDs.
   */
  public async getSkillsByIds(ids: number[]): Promise<Skill[]> {
    if (!ids || ids.length === 0) return [];
    const result = await this.find<any>('skills', 1, { id: ids });
    return result?.data || [];
  }

  /**
   * Get certifications by an array of IDs.
   */
  public async getCertificationsByIds(ids: number[]): Promise<Certification[]> {
    if (!ids || ids.length === 0) return [];
    const result = await this.find<any>('certifications', 1, { id: ids });
    return result?.data || [];
  }

  /**
   * Get developments (soft skills) by an array of IDs.
   */
  public async getDevelopmentsByIds(ids: number[]): Promise<Development[]> {
    if (!ids || ids.length === 0) return [];
    const result = await this.find<any>('developments', 1, { id: ids });
    return result?.data || [];
  }

  /**
   * Get industries by an array of IDs.
   */
  public async getIndustriesByIds(ids: number[]): Promise<Industry[]> {
    if (!ids || ids.length === 0) return [];
    const result = await this.find<any>('industries', 1, { id: ids });
    return result?.data || [];
  }

  /**
   * Get domains by an array of IDs.
   */
  public async getDomainsByIds(ids: number[]): Promise<Domain[]> {
    if (!ids || ids.length === 0) return [];
    const result = await this.find<any>('domains', 1, { id: ids });
    return result?.data || [];
  }

  // ── Team config (department nesting + managers) ───────────────────────
  // All four reject on error after errorify has toasted the server's message.

  public async setParent(id: number, parentId: number | null): Promise<{id:number; parent_id:number|null}> {
    return await this.put<{parent_id:number|null}, {id:number; parent_id:number|null}>(
      '/departments/' + id + '/parent', { parent_id: parentId });
  }

  public async listManagers(id: number): Promise<{User_id:number; name:string}[]> {
    return await this.get<{User_id:number; name:string}[]>(
      '/departments/' + id + '/managers', 'Could not load managers');
  }

  public async assignManager(id: number, userId: number): Promise<{Department_id:number; User_id:number}> {
    return await this.post<{User_id:number}, {Department_id:number; User_id:number}>(
      '/departments/' + id + '/managers', { User_id: userId });
  }

  public async removeManager(id: number, userId: number): Promise<{removed:boolean}> {
    return await this.delete<{removed:boolean}>(
      '/departments/' + id + '/managers/' + userId);
  }
}
